import { Zap, LogOut, LogIn, RefreshCw, ShieldCheck, Shield, UserRound, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import DataRightsModal from "@/components/DataRightsModal";
import ValueFoundTicker from "@/components/ValueFoundTicker";

export default function Header({ credits, plan, onUpgrade, onSync, creditFx = 0 }) {
  const { user, login, logout } = useAuth();
  const isPro = plan === "pro";
  // Tier labels align with new pricing (Free / Thrifter / Reseller / Pro).
  const tierName = { thrifter: "Thrifter", reseller: "Reseller", pro: "Pro" }[plan];
  const scanWord = credits === 1 ? "scan" : "scans";
  const label = tierName
    ? `${tierName} · ${credits} ${scanWord} left`
    : `${credits} ${scanWord} left${plan === "free" ? " (Free)" : ""}`;
  const low = credits <= 3 && plan !== "pro";

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 border-b border-[#E9ECEF] bg-white/85 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 px-3 sm:px-5 md:px-8 py-3 sm:py-4">
        <div className="flex items-center gap-3 min-w-0">
          <a href="/" aria-label="rsell ai — home" className="block flex-shrink-0" data-testid="app-logo">
            <img
              src="/rsell-logo-v5.png"
              alt="rsell ai"
              className="h-8 sm:h-9 w-auto select-none pointer-events-none"
              draggable={false}
            />
          </a>
          <div className="hidden sm:block"><ValueFoundTicker /></div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          <div className="hidden sm:block"><SyncCreditsButton onSync={onSync} /></div>
          <div className="relative">
          <AnimatePresence>
            {creditFx > 0 && (
              <motion.div
                key={creditFx}
                initial={{ opacity: 0, y: 0, scale: 0.9 }}
                animate={{ opacity: [0, 1, 1, 0], y: -28, scale: 1 }}
                transition={{ duration: 0.9, ease: "easeOut", times: [0, 0.15, 0.55, 1] }}
                className="absolute -top-2 -left-2 z-50 px-2 py-0.5 rounded-full bg-ink-900 text-accent-on-dark text-[11px] font-bold font-mono-num pointer-events-none shadow-card"
              >
                -1 credit
              </motion.div>
            )}
          </AnimatePresence>
          <button
            data-testid="credit-pill"
            onClick={onUpgrade}
            aria-label={low ? "Low on scans — tap to top up or upgrade" : "Scans remaining — tap to view plans"}
            className={
              "flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 min-h-[36px] rounded-full border transition-colors " +
              (low
                ? "bg-warning-soft border-warning-fill text-warning-text"
                : "bg-ink-900 border-ink-900 text-ink-on-dark hover:bg-[#1E293B]")
            }
          >
            <Zap size={13} className={low ? "text-warning-fill" : "text-accent-on-dark"} fill="currentColor" />
            <span className="hidden sm:inline text-xs md:text-sm font-medium font-mono-num">{label}</span>
            <span className="sm:hidden text-xs font-bold font-mono-num">
              {credits} <span className="font-medium">scans</span>
            </span>
            <ChevronRight size={13} className="sm:hidden opacity-70 -ml-0.5" strokeWidth={2.5} />
          </button>
          </div>
          <button
            data-testid="upgrade-btn"
            onClick={onUpgrade}
            className="hidden sm:inline-flex pill-accent-button px-4 py-2 text-sm whitespace-nowrap"
          >
            Upgrade
          </button>

          {user ? <UserMenu user={user} onLogout={logout} onSync={onSync} /> : (
            <button
              data-testid="google-signin-btn"
              onClick={login}
              aria-label="Sign in with Google"
              title="Sign in"
              className="flex items-center justify-center gap-1.5 rounded-full bg-white border border-[#E9ECEF] hover:bg-[#F1F5F9] hover:border-[#CBD5E1] transition-colors w-10 h-10 sm:w-auto sm:h-auto sm:px-3.5 sm:py-2 text-xs sm:text-sm font-semibold text-[#0F172A]"
            >
              <UserRound size={18} strokeWidth={2.25} style={{ color: "#0F172A", stroke: "#0F172A" }} />
              <span className="hidden sm:inline">Sign in</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function SyncCreditsButton({ onSync }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (!onSync || busy) return;
    setBusy(true);
    try {
      const r = await onSync();
      if (r && r.newCreditsGranted > 0) {
        // Celebration modal will fire from AppMain; no toast needed.
      } else {
        toast.success("You're up to date");
      }
    } catch {
      toast.error("Sync failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid="sync-credits-btn"
      onClick={click}
      disabled={busy}
      title="Pull latest credits from Whop"
      aria-label="Sync Credits"
      className="pill-button flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs font-medium text-white/85"
    >
      <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
      <span className="hidden sm:inline">{busy ? "Syncing…" : "Sync Credits"}</span>
      <span className="sm:hidden">{busy ? "…" : "Sync"}</span>
    </button>
  );
}

function UserMenu({ user, onLogout, onSync }) {
  const [open, setOpen] = useState(false);
  const [dataModal, setDataModal] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 p-1 rounded-full hover:bg-surface-sunken transition-colors"
      >
        {user.picture ? (
          <img alt="" src={user.picture} className="w-8 h-8 rounded-full border border-white/15" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#10B981] flex items-center justify-center text-white text-xs font-bold">
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="user-menu"
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-64 rounded-ds-md bg-surface-card border border-border-default p-3 shadow-modal"
          >
            <div className="px-2 py-2">
              <div className="text-sm font-semibold text-ink-900 truncate" data-testid="user-menu-name">{user.name || "Signed in"}</div>
              <div className="text-xs text-ink-500 truncate">{user.email}</div>
            </div>
            <div className="h-px bg-border-default my-1" />
            {/* Admin — visible only for allowlisted emails. The route itself
                is server-gated, this just gives admins a discoverable path. */}
            {user.is_admin && (
              <Link
                data-testid="admin-link"
                to="/admin"
                onClick={() => setOpen(false)}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-ink-700 hover:bg-surface-sunken transition-colors"
              >
                <Shield size={14} /> Admin
              </Link>
            )}
            <button
              data-testid="menu-sync-btn"
              onClick={async () => {
                setOpen(false);
                try {
                  const r = await onSync?.();
                  if (!r || !r.newCreditsGranted) toast.success("You're up to date");
                } catch { toast.error("Sync failed"); }
              }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-ink-700 hover:bg-surface-sunken transition-colors"
            >
              <RefreshCw size={14} /> Sync Credits
            </button>
            <button
              data-testid="my-data-btn"
              onClick={() => { setOpen(false); setDataModal(true); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-ink-700 hover:bg-surface-sunken transition-colors"
            >
              <ShieldCheck size={14} /> My data &amp; privacy
            </button>
            <button
              data-testid="logout-btn"
              onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-ink-700 hover:bg-surface-sunken transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <DataRightsModal open={dataModal} onClose={() => setDataModal(false)} onDeleted={onLogout} />
    </div>
  );
}
